<template>
    <div ref="customerInfoAndPayment">
        <div class="calendar-summary-item tw-my-4 tw-text-center">
            <!-- toggle preview -->
            <a
                class="tw-text-base tw-underline tw-font-semibold preview"
                href="#"
                @click.prevent="longPreview = !longPreview"
            >
                <span>{{
                    !longPreview
                        ? translate('customerInfoAndPayment.see_details')
                        : translate('customerInfoAndPayment.hide_details')
                }}</span>
            </a>
        </div>
        <preview v-show="longPreview" @change-view="emitAndHidePreview" />
        <div v-show="!longPreview">
            <div class="tw-mb-4">
                <h4 class="tw-mb-4 tw-text-lg tw-font-semibold tw-leading-6 tw-text-center">
                    {{ translate('customerInfoAndPayment.title') }}
                </h4>
            </div>
            <div class="tw-flex tw-flex-row tw-items-center tw-justify-between preview">
                <h4 class="tw-text-base tw-font-semibold">
                    {{ translate('customerInfoAndPayment.steps.personal') }}
                </h4>
                <a
                    href="#"
                    @click.prevent="changeView(BOOKING_VIEWS.CUSTOMER_VIEW)"
                    class="tw-text-base tw-underline tw-font-semibold"
                    v-show="showPaymentActionView"
                >
                    {{ translate('customerInfoAndPayment.edit') }}
                </a>
            </div>
            <keep-alive>
                <customer-info
                    v-if="showCustomerView"
                    :active-view="activeView"
                    @change-view="changeView($event)"
                    @error="$emit('error', $event)"
                />
            </keep-alive>
            <!-- payment start -->
            <div class="tw-my-4" v-if="showPaymentActionView">
                <h4 class="tw-text-base tw-font-semibold tw-mt-8">
                    {{ translate('customerInfoAndPayment.steps.payment') }}
                </h4>
                <div class="tw-mt-8 tw-text-center" v-if="isOnlinePaymentEligible">
                    <stripe :id="id" @successBooking="$emit('successBooking')"></stripe>
                    <a
                        href="#"
                        @click.prevent="loadQrCode"
                        class="tw-text-base tw-underline tw-font-semibold tw-inline-block tw-my-4 coupon-code"
                    >
                        {{ translate('customerInfoAndPayment.buttons.qrcode') }}
                    </a>
                    <div v-html="qrcode" class="tw-w-fit tw-mx-auto tw-my-4"></div>
                    <p class="checkout-disclaimer tw-mt-8 tw-mb-2">
                        {{ translate('customerInfoAndPayment.payment.purchase_info') }}
                    </p>
                    <p class="checkout-disclaimer tw-mt-2 tw-mb-8">
                        {{ translate('customerInfoAndPayment.payment.currency_info') }}
                    </p>
                </div>
                <!-- pay later -->
                <div v-else>
                    <!-- non card (direct) payment info -->
                    <div
                        class="tw-text-base tw-leading-tight tw-text-center tw-rounded-4 tw-p-2 tw-text-dark-grey tw-mt-4"
                    >
                        {{ translate('customerInfoAndPayment.payment.direct_payment_info') }}
                    </div>
                    <div class="tw-my-8 tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-justify-center">
                        <span class="tw-text-xl tw-font-semibold tw-uppercase total-price"
                            >{{ translate('offers.total') }}:</span
                        >
                        <span class="tw-text-xl tw-font-semibold tw-uppercase total-price">
                            {{ totalPrice }}&nbsp;{{ currencyCode }}
                        </span>
                    </div>
                    <!-- <div class="tw-text-base tw-leading-tight tw-text-center tw-rounded-4 tw-p-2 tw-text-dark-grey">
                        {{ translate('customerInfoAndPayment.payment.total_in_hrk') }}
                    </div>
                    <div class="tw-mb-10 tw-text-2xl tw-font-semibold tw-text-dark-green tw-text-center">
                        {{ totalPrice }}
                    </div> -->
                    <!-- BE error messages -->
                    <div class="error-message" v-for="error in processBookingErrors" :key="error">
                        <div>
                            <img :src="WarningIcon" alt="Warning" />
                        </div>
                        <div>
                            <span>{{ error }}</span>
                        </div>
                    </div>
                    <div class="tw-my-4">
                        <button
                            @click="confirmReservation"
                            class="btn btn-tertiary btn-md btn-fw"
                            :disabled="showSpinner"
                        >
                            <loader v-if="showSpinner" :size="16" :border-width="4" />
                            <span v-else>{{ translate('customerInfoAndPayment.payment.buttons.confirm') }}</span>
                        </button>
                    </div>
                </div>
            </div>
            <!-- end payment -->
        </div>
    </div>
</template>

<script>
import i18n from '@js/vue/mixins/i18n';
import Stripe from './Stripe.vue';
import CustomerInfo from './CustomerInfo.vue';
import Preview from './Preview.vue';
import { BOOKING_VIEWS } from '@js/vue/booking-views.js';
import { mapGetters } from 'vuex';
import WarningIcon from '@images/warning-24px.svg';
import Loader from './shared/Loader.vue';

export default {
    name: 'CustomerInfoAndPayment',
    mixins: [i18n],
    inject: ['lang', 'translations', 'currencyCode'],
    emits: ['error', 'successBooking', 'changeView'],
    components: {
        Preview,
        Stripe,
        CustomerInfo,
        Loader,
    },
    data() {
        return {
            WarningIcon,
            activeView: BOOKING_VIEWS.CUSTOMER_VIEW,
            longPreview: false,
            processBookingErrors: [],
            customerInfoBackendErrors: {},
            qrcode: '',
            BOOKING_VIEWS,
            showSpinner: false,
        };
    },
    computed: {
        ...mapGetters('booking', [
            'getServiceId',
            'getCustomer',
            'getOnlinePaymentEligibility',
            'getRegionCategorySlugs',
            'getTotalPrice',
        ]),
        id() {
            return this.getServiceId;
        },
        customer() {
            return this.getCustomer;
        },
        showPaymentActionView() {
            return this.activeView === BOOKING_VIEWS.PAYMENT_ACTION_VIEW;
        },
        showCustomerView() {
            return this.activeView === BOOKING_VIEWS.CUSTOMER_VIEW;
        },
        isOnlinePaymentEligible() {
            return this.getOnlinePaymentEligibility;
        },
        regionCategorySlugs() {
            return this.getRegionCategorySlugs;
        },
        totalPrice() {
            return this.getTotalPrice;
        },
    },
    mounted() {
        let wrapper = document.getElementsByClassName('modal-content-wrapper')[0];
        wrapper.scrollIntoView();
    },
    methods: {
        changeView(view) {
            this.activeView = view;
        },
        emitAndHidePreview() {
            this.$emit('changeView', BOOKING_VIEWS.OFFERS, true);
            this.longPreview = false;
        },
        loadQrCode() {
            axios.get('/booking/qrcode/generate').then((response) => {
                this.qrcode = response.data;
            });
        },
        confirmReservation() {
            const data = {
                onlinePayment: false,
            };
            this.showSpinner = true;
            axios
                .post(`/booking/${this.id}/process`, data)
                .then((response) => {
                    dataLayer.push({
                        event: 'bookingSuccesfull',
                        experience: this.title,
                        location: this.regionCategorySlugs['region-slug'],
                        experienceType: this.regionCategorySlugs['category-slug'],
                        price: this.totalPrice,
                    });
                    this.$emit('successBooking');
                })
                .catch((error) => {
                    console.log('error', error);
                    const { status } = error.response;
                    if (status === 422) {
                        // unprocessable data
                        dataLayer.push({
                            event: 'bookingFailed',
                            experience: this.title,
                            errorMessage: error.response.data.errors,
                        });
                        this.processBookingErrors = error.response.data.errors;
                    } else if (status === 419 || status === 404) {
                        // 419 csrf token mismatch (session expired)
                        // 404 cart not in session (probably expired session)
                        dataLayer.push({
                            event: 'bookingFailed',
                            experience: this.title,
                            errorMessage: 'session expired',
                        });
                        this.$emit('error', 'session_expired');
                    } else {
                        // something else went wrong
                        console.dir(error);
                        dataLayer.push({
                            event: 'bookingFailed',
                            experience: this.title,
                            errorMessage: 'something went wrong',
                        });
                        this.$emit('error', 'something_went_wrong');
                    }
                })
                .finally(() => (this.showSpinner = false));
        },
    },
};
</script>
